/**
 * A Module for miscellaneous helper functions
 *
 */

import scriptjs from "scriptjs";

/**
 * Ad container sizes that are not for showig a regular banner ad, but the size is used to signify some special behaviour
 */
const SPECIAL_SIZES = [[8, 8]];

const DEFAULT_BREAKPOINTS = [
  {
    name: "baseLayout",
    width: -1,
  },
  {
    name: "desktop",
    width: 1007,
  },
];

/**
 * Enum for return values of the getShape() func
 */
const Shape = {
  WIDE: "wide",
  TALL: "tall",
  MPU: "mpu",
  SPECIAL: "special",
};

/**
 * Measure the size of an HTMLElement and decide whether it's wide or tall etc
 * @param {number} width
 * @param {number} height
 * @returns {string} a member of the Shape.x enum
 */
export function getShape(width, height) {
  const tallWide = height > width ? Shape.TALL : Shape.WIDE;
  const isSpecial = SPECIAL_SIZES.some(([w, h]) => w === width && h === height);
  // DMPU counts as MPU here
  const isMPU =
    width === 300 && (withinFive(255, height) || withinFive(605, height));

  return isSpecial ? Shape.SPECIAL : isMPU ? Shape.MPU : tallWide;
}

/**
 * Load a script tag and return a promise that resolves when the script is loaded.
 * a simple wrapper for $script to make it return a Promise
 * @param {string} urlString
 * @returns {promise}
 */
export function loadScript(urlString) {
  return new Promise((resolve) => {
    scriptjs(urlString, resolve);
  });
}

/**
 * Load a css link tag and return a promise that resolves when the css is loaded.
 * @param {string} urlString
 * @returns {promise}
 */
export function loadCss(urlString) {
  return new Promise((resolve) => {
    const tag = document.createElement("link");
    tag.rel = "stylesheet";
    tag.onload = resolve;
    tag.href = urlString;
    document.getElementsByTagName("HEAD")[0].appendChild(tag);
  });
}
/**
 * returns window.location.href
 * So we can mock it for unit testing
 * @returns {string}
 */
export function getLocationHref() {
  return window.location.href;
}

/**
 * returns the current query string as a URLSearchParams object
 * @returns {URLSearchParams}
 */
export function getSearchParams() {
  return new URL(getLocationHref()).searchParams;
}

/**
 * returns the value of document.currentScript
 * So we can mock it for unit testing
 * @returns {HTMLScriptElement|null}
 */
export function getCurrentScript() {
  return document.currentScript || { src: null };
}

/**
 * Measure the size of an HTMLElement and return the size as an array
 * @param {HTMLElement} elem
 * @returns {array} array with width {number} and height {number} properties
 */
export function getElemWidthHeight(elem) {
  return [elem.offsetWidth, elem.offsetHeight];
}

/**
 * Detect the width of the browser window and determine which layout breakpoint should be used,
 * based on thresholds in the config.
 * @returns {string} name of the breakpoint, from config
 */
export function getBreakpoint(bps = DEFAULT_BREAKPOINTS) {
  const width = window.innerWidth;
  var bpLength = bps.length;
  var i;
  for (i = 0; i < bpLength; i++) {
    if (width < bps[i].width) {
      return bps[i - 1].name;
    }
  }
  return bps[bpLength - 1].name;
}

/**
 * Helper func to determie whether twonumbers are within five of each other
 * @param {number} a
 * @param {number} b
 * @returns {boolean}
 */
export function withinFive(a, b) {
  return Math.abs(a - b) <= 5;
}

/**
 * Helper function to determine whether two arrays representing dimensions of HTMLElements are identical
 * @param {array} a for example [300, 250]
 * @param {array} b
 * @returns {boolean}
 */
export function isSameSize(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (e) {
    return false;
  }
}
